import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Modal, Spinner } from "react-bootstrap";
import {
  FiBriefcase,
  FiCalendar,
  FiCopy,
  FiDownload,
  FiEdit2,
  FiFileText,
  FiMail,
  FiMessageSquare,
  FiPhone,
  FiPlus,
  FiRotateCcw,
  FiSearch,
  FiTrash2,
  FiUpload,
  FiUsers,
} from "react-icons/fi";
import { FaFileExcel } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";

import { getApiUrl } from "../../utils/apiUrl";
import { getCsrfToken } from "../../utils/getCsrfToken";
import "./OrganizacionesPage.css";

const PAGE_SIZE = 10;

const organizationLabel = (organization) => (
  organization?.nombre_completo
  || `${organization?.nombre || ""}${organization?.subnombre ? ` (${organization.subnombre})` : ""}`
);

const initials = (name) => String(name || "OR")
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part.charAt(0).toUpperCase())
  .join("");

const OrganizationAvatar = ({ organization, large = false }) => (
  <div className={`organization-avatar${large ? " organization-avatar--large" : ""}`}>
    {organization?.imagen
      ? <img src={organization.imagen} alt={`Logo de ${organizationLabel(organization)}`} />
      : <span>{initials(organizationLabel(organization))}</span>}
  </div>
);

const ContactAvatar = ({ contact }) => (
  <div className="organization-contact-avatar">
    {contact.imagen
      ? <img src={contact.imagen} alt={contact.nombre} />
      : <span>{initials(contact.nombre)}</span>}
  </div>
);

const formatDate = (value) => {
  if (!value) return "Sin fecha";
  const [year, month, day] = value.split("-");
  return `${day}-${month}-${year}`;
};

const formatDateTime = (value) => {
  if (!value) return "Sin fecha";
  return new Intl.DateTimeFormat("es-CL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const firstError = (payload, fallback) => {
  if (!payload || typeof payload !== "object") return fallback;
  const value = Object.values(payload)[0];
  if (Array.isArray(value)) return value[0];
  if (typeof value === "string") return value;
  return fallback;
};

const OrganizacionesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("contactos");
  const [programYear, setProgramYear] = useState("todos");
  const [initiativeYear, setInitiativeYear] = useState("todos");
  const [showCreate, setShowCreate] = useState(false);
  const [showField, setShowField] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [removeContactImage, setRemoveContactImage] = useState(false);
  const [logoReplacement, setLogoReplacement] = useState("");
  const [contactImageReplacement, setContactImageReplacement] = useState("");
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentError, setCommentError] = useState("");
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [documentFile, setDocumentFile] = useState(null);
  const [documentError, setDocumentError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");
  const selectedOrganizationId = selected?.id;

  const loadOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(getApiUrl("api/organizaciones-gestion/"), {
        credentials: "include",
      });
      if (!response.ok) throw new Error("No se pudieron cargar las organizaciones.");
      setOrganizations(await response.json());
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrganizations(); }, [loadOrganizations]);

  useEffect(() => {
    let active = true;
    if (!id) {
      setSelected(null);
      return () => { active = false; };
    }
    setDetailLoading(true);
    fetch(getApiUrl(`api/organizaciones-gestion/${id}/`), { credentials: "include" })
      .then((response) => {
        if (!response.ok) throw new Error("No se pudo cargar la organización.");
        return response.json();
      })
      .then((data) => { if (active) setSelected(data); })
      .catch((requestError) => { if (active) setError(requestError.message); })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    setProgramYear("todos");
    setInitiativeYear("todos");
    setActiveTab("contactos");
    setShowCommentForm(false);
    setCommentText("");
    setCommentError("");
    setShowDocumentForm(false);
    setDocumentName("");
    setDocumentFile(null);
    setDocumentError("");
  }, [selectedOrganizationId]);

  const filtered = useMemo(() => {
    const search = query.trim().toLocaleLowerCase("es");
    if (!search) return organizations;
    return organizations.filter((organization) => (
      organizationLabel(organization).toLocaleLowerCase("es").includes(search)
      || organization.holding?.nombre_completo?.toLocaleLowerCase("es").includes(search)
    ));
  }, [organizations, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginatedOrganizations = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);
  const visiblePageNumbers = useMemo(() => {
    const values = new Set([1, totalPages]);
    for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
      if (page >= 1 && page <= totalPages) values.add(page);
    }
    return [...values].sort((left, right) => left - right);
  }, [currentPage, totalPages]);

  useEffect(() => { setCurrentPage(1); }, [query]);
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const updateLocalOrganization = (updated) => {
    setSelected((current) => current ? { ...current, ...updated } : updated);
    setOrganizations((current) => current
      .map((organization) => organization.id === updated.id
        ? { ...organization, ...updated }
        : organization)
      .sort((left, right) => (
        (right.iniciativas_count || 0) - (left.iniciativas_count || 0)
        || organizationLabel(left).localeCompare(organizationLabel(right), "es")
      )));
  };

  const createOrganization = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const formData = new FormData(event.currentTarget);
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl("api/organizaciones-gestion/"), {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrfToken },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(firstError(data, "No se pudo agregar la organización."));
      setOrganizations((current) => [...current, data].sort((left, right) => (
        (right.iniciativas_count || 0) - (left.iniciativas_count || 0)
        || organizationLabel(left).localeCompare(organizationLabel(right), "es")
      )));
      setShowCreate(false);
      navigate(`/organizaciones/${data.id}`);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const openFieldEditor = (field) => {
    setEditingField(field);
    setRemoveLogo(false);
    setLogoReplacement("");
    setError("");
    setShowField(true);
  };

  const saveField = async (event) => {
    event.preventDefault();
    if (!selected || !editingField) return;
    setBusy(true);
    setError("");
    const formData = new FormData(event.currentTarget);
    if (editingField === "logo" && removeLogo) formData.append("remove_logo", "true");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/organizaciones-gestion/${selected.id}/`), {
        method: "PATCH",
        credentials: "include",
        headers: { "X-CSRFToken": csrfToken },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(firstError(data, "No se pudo guardar el cambio."));
      updateLocalOrganization(data);
      setShowField(false);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const openContact = (contact = null) => {
    setEditingContact(contact);
    setRemoveContactImage(false);
    setContactImageReplacement("");
    setError("");
    setShowContact(true);
  };

  const saveContact = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const formData = new FormData(event.currentTarget);
    const contactImage = formData.get("imagen");
    if (!contactImage?.size) formData.delete("imagen");
    if (editingContact && removeContactImage) formData.append("remove_imagen", "true");
    const endpoint = editingContact
      ? `api/organizaciones-gestion/${selected.id}/contactos/${editingContact.id}/`
      : `api/organizaciones-gestion/${selected.id}/contactos/`;
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(endpoint), {
        method: editingContact ? "PATCH" : "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrfToken },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(firstError(data, "No se pudo guardar el contacto."));
      setSelected((current) => {
        const contacts = editingContact
          ? current.contactos.map((contact) => contact.id === data.id ? data : contact)
          : [...current.contactos, data];
        return { ...current, contactos: contacts.sort((a, b) => a.nombre.localeCompare(b.nombre, "es")) };
      });
      setShowContact(false);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const addComment = async (event) => {
    event.preventDefault();
    if (!selected || !commentText.trim()) {
      setCommentError("Escribe un comentario antes de guardar.");
      return;
    }
    setBusy(true);
    setCommentError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(
        getApiUrl(`api/organizaciones-gestion/${selected.id}/comments/`),
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify({ comment_text: commentText.trim() }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(firstError(data, "No se pudo guardar el comentario."));
      }
      updateLocalOrganization(data);
      setCommentText("");
      setShowCommentForm(false);
    } catch (requestError) {
      setCommentError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const addDocument = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!selected || !documentFile) {
      setDocumentError("Selecciona un documento antes de guardar.");
      return;
    }
    setBusy(true);
    setDocumentError("");
    const formData = new FormData();
    formData.append("documento", documentFile);
    if (documentName.trim()) formData.append("nombre", documentName.trim());
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(
        getApiUrl(`api/organizaciones-gestion/${selected.id}/documents/`),
        {
          method: "POST",
          credentials: "include",
          headers: { "X-CSRFToken": csrfToken },
          body: formData,
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(firstError(data, "No se pudo subir el documento."));
      }
      updateLocalOrganization(data);
      setDocumentName("");
      setDocumentFile(null);
      setShowDocumentForm(false);
      form.reset();
    } catch (requestError) {
      setDocumentError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const copyValue = async (value, key) => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopied(key);
    window.setTimeout(() => setCopied(""), 1600);
  };

  const downloadExcel = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch(
        getApiUrl(`api/organizaciones-gestion/${selected.id}/excel/`),
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("No se pudo descargar el Excel.");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Organizacion-${organizationLabel(selected)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const renderFieldInput = () => {
    if (editingField === "logo") {
      return (
        <div className="organization-media-editor">
          <label>Nuevo logo<input className="form-control" type="file" name="logo" accept="image/*" required={!selected?.imagen} onChange={(event) => { setLogoReplacement(event.target.files?.[0]?.name || ""); setRemoveLogo(false); }} /></label>
          {logoReplacement && <small className="organization-replacement-file"><FiUpload /> Reemplazo seleccionado: {logoReplacement}</small>}
          {selected?.imagen && !logoReplacement && (
            <div className={`organization-current-media${removeLogo ? " is-removed" : ""}`}>
              {!removeLogo && <img src={selected.imagen} alt="Logo actual" />}
              <span><strong>{removeLogo ? "El logo se quitará al guardar" : "Logo actual"}</strong><small>{removeLogo ? "Puedes deshacer esta decisión." : "Se conservará si no eliges otro."}</small></span>
              <button type="button" onClick={() => setRemoveLogo((current) => !current)}>{removeLogo ? <><FiRotateCcw /> Deshacer</> : <><FiTrash2 /> Quitar</>}</button>
            </div>
          )}
        </div>
      );
    }
    if (editingField === "holding") {
      return (
        <select className="form-select" name="holding_id" defaultValue={selected?.holding?.id || ""}>
          <option value="">Sin holding</option>
          {organizations.filter((item) => item.id !== selected?.id).map((item) => (
            <option key={item.id} value={item.id}>{organizationLabel(item)}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        className="form-control"
        name={editingField}
        defaultValue={selected?.[editingField] || ""}
        required={editingField === "nombre"}
        autoFocus
      />
    );
  };

  const programs = selected?.trayectoria?.filter((item) => item.es_programa) || [];
  const initiatives = selected?.trayectoria?.filter((item) => !item.es_programa) || [];
  const yearsFor = (items) => [...new Set(
    items.map((item) => item.f_inicio?.slice(0, 4)).filter(Boolean),
  )].sort((left, right) => Number(right) - Number(left));
  const programYears = yearsFor(programs);
  const initiativeYears = yearsFor(initiatives);
  const filteredPrograms = programYear === "todos"
    ? programs
    : programs.filter((item) => item.f_inicio?.startsWith(programYear));
  const filteredInitiatives = initiativeYear === "todos"
    ? initiatives
    : initiatives.filter((item) => item.f_inicio?.startsWith(initiativeYear));
  const isUpcoming = (item) => {
    if (!item.f_inicio) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(`${item.f_inicio}T00:00:00`) > today;
  };

  const TrajectoryList = ({ items, empty }) => items.length ? (
    <div className="organization-trajectory-list">
      {items.map((item) => {
        const rate = item.inscritas ? Math.round((item.aprobadas / item.inscritas) * 100) : 0;
        return (
          <button
            type="button"
            key={item.id}
            className="organization-trajectory-item"
            onClick={() => navigate(`/${item.es_programa ? "programas" : "iniciativas"}/${item.id}`)}
          >
            <div>
              <div className="organization-trajectory-title">
                <strong>{item.nombre}</strong>
                {isUpcoming(item) && <span>Próximamente</span>}
              </div>
              <span><FiCalendar /> {formatDate(item.f_inicio)} · {item.comuna || item.region || "Sin ubicación"}</span>
              <small>{item.roles.join(" · ")}</small>
            </div>
            <div className="organization-trajectory-numbers">
              <span>{item.inscritas} inscritas</span>
              <span>{item.finalizada ? `${rate}% aprobación` : "Resultado pendiente"}</span>
            </div>
            {item.finalizada && (
              <div className="organization-approval-bar" aria-label={`${rate}% de aprobación`}>
                <span className="approved" style={{ width: `${rate}%` }} />
                <span className="not-approved" style={{ width: `${100 - rate}%` }} />
              </div>
            )}
          </button>
        );
      })}
    </div>
  ) : <p className="organization-empty">{empty}</p>;

  return (
    <main className="organizations-page">
      <section className="organizations-hero">
        <div>
          <span>VÍNCULOS INSTITUCIONALES</span>
          <h1>Organizaciones</h1>
          <p>Empresas y entidades que participan en las iniciativas de RedMaestra.</p>
        </div>
        <button type="button" onClick={() => { setError(""); setShowCreate(true); }}>
          <FiPlus /> Agregar organización
        </button>
      </section>

      <section className="organizations-toolbar">
        <label>
          <FiSearch />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar organización..." />
        </label>
        <output>{filtered.length}</output>
      </section>

      {error && !showCreate && !showField && !showContact && <Alert variant="danger">{error}</Alert>}
      {loading ? (
        <div className="organizations-state"><Spinner animation="border" /><span>Cargando organizaciones...</span></div>
      ) : filtered.length === 0 ? (
        <div className="organizations-state"><FiBriefcase /><strong>No encontramos organizaciones</strong></div>
      ) : (
        <section className="organizations-table">
          <div className="organizations-table-header"><span aria-hidden="true" /><span>Organización</span><span>Trayectoria</span><span>Aprobación promedio</span></div>
          <div className="organizations-grid">
            {paginatedOrganizations.map((organization) => (
              <button type="button" className="organization-card" key={organization.id} onClick={() => navigate(`/organizaciones/${organization.id}`)}>
                <OrganizationAvatar organization={organization} />
                <div>
                  <strong>{organizationLabel(organization)}</strong>
                  {organization.holding && <span>Holding: {organization.holding.nombre_completo}</span>}
                </div>
                <span className="organization-count"><b>{organization.iniciativas_count || 0}</b> iniciativas</span>
                <span className="organization-average">
                  <b>{organization.tasa_aprobacion === null ? "—" : `${organization.tasa_aprobacion}%`}</b>
                  <small>{organization.tasa_aprobacion === null ? "Sin resultados" : `${organization.aprobadas_finalizadas}/${organization.inscritas_finalizadas}`}</small>
                </span>
              </button>
            ))}
          </div>
          {totalPages > 1 && (
            <nav className="organizations-pagination" aria-label="Páginas de organizaciones">
              <button type="button" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1}>Anterior</button>
              <div>
                {visiblePageNumbers.map((page, index) => (
                  <React.Fragment key={page}>
                    {index > 0 && page - visiblePageNumbers[index - 1] > 1 && <span>…</span>}
                    <button type="button" className={currentPage === page ? "active" : ""} aria-current={currentPage === page ? "page" : undefined} onClick={() => setCurrentPage(page)}>{page}</button>
                  </React.Fragment>
                ))}
              </div>
              <button type="button" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage === totalPages}>Siguiente</button>
            </nav>
          )}
        </section>
      )}

      {detailLoading && <div className="organizations-detail-loading"><Spinner animation="border" /></div>}
      <Modal show={Boolean(selected)} onHide={() => !busy && navigate("/organizaciones")} backdrop="static" keyboard={!busy} dialogClassName="organization-detail-modal">
        <Modal.Header closeButton={!busy} className="organization-detail-close" />
        <Modal.Body>
          {selected && (
            <>
              {busy && <div className="organization-busy"><Spinner animation="border" /><span>Procesando...</span></div>}
              <section className="organization-detail-hero">
                <button type="button" className="organization-edit-logo" onClick={() => openFieldEditor("logo")} disabled={busy} title="Cambiar logo">
                  <OrganizationAvatar organization={selected} large />
                  <FiEdit2 />
                </button>
                <div className="organization-detail-name">
                  <button type="button" onClick={() => openFieldEditor("nombre")} disabled={busy}>
                    <h2>{selected.nombre}</h2><FiEdit2 />
                  </button>
                  <button type="button" onClick={() => openFieldEditor("subnombre")} disabled={busy}>
                    <span>{selected.subnombre ? `(${selected.subnombre})` : "Agregar subnombre"}</span><FiEdit2 />
                  </button>
                  <button type="button" onClick={() => openFieldEditor("holding")} disabled={busy}>
                    <small>{selected.holding ? `Holding: ${selected.holding.nombre_completo}` : "Sin holding"}</small><FiEdit2 />
                  </button>
                </div>
                <button type="button" className="organization-excel" onClick={downloadExcel} disabled={busy}>
                  <FaFileExcel /> Excel
                </button>
              </section>

              <nav className="organization-tabs" aria-label="Secciones de la organización">
                <button type="button" className={activeTab === "contactos" ? "is-active" : ""} aria-pressed={activeTab === "contactos"} onClick={() => setActiveTab("contactos")}>
                  <FiUsers /> Contactos
                </button>
                <button type="button" className={activeTab === "trayectoria" ? "is-active" : ""} aria-pressed={activeTab === "trayectoria"} onClick={() => setActiveTab("trayectoria")}>
                  <FiBriefcase /> Trayectoria
                </button>
                <button type="button" className={activeTab === "comentarios" ? "is-active" : ""} aria-pressed={activeTab === "comentarios"} onClick={() => setActiveTab("comentarios")}>
                  <FiMessageSquare /> Comentarios ({selected.comentarios?.length || 0})
                </button>
                <button type="button" className={activeTab === "documentos" ? "is-active" : ""} aria-pressed={activeTab === "documentos"} onClick={() => setActiveTab("documentos")}>
                  <FiFileText /> Documentos ({selected.documentos?.length || 0})
                </button>
              </nav>

              {activeTab === "contactos" && (
                <section className="organization-tab-panel">
                  <div className="organization-tab-heading">
                    <div><span>CONTACTOS PROFESIONALES</span><h3>Personas de contacto</h3></div>
                    <button type="button" className="organization-tab-add-contact" onClick={() => openContact()} disabled={busy}>
                      <FiPlus /> Agregar contacto
                    </button>
                  </div>
                  {selected.contactos?.length ? (
                    <div className="organization-contacts-grid">
                      {selected.contactos.map((contact) => (
                        <article className="organization-contact-card" key={contact.id}>
                          <button type="button" className="organization-contact-main" onClick={() => openContact(contact)} title="Editar contacto">
                            <ContactAvatar contact={contact} />
                            <div><strong>{contact.nombre}</strong></div>
                            <FiEdit2 />
                          </button>
                          <div className="organization-contact-line">
                            <FiPhone /><span>{contact.celular || "No informado"}</span>
                            {contact.celular && <button type="button" onClick={() => copyValue(contact.celular, `phone-${contact.id}`)} title="Copiar celular"><FiCopy /></button>}
                          </div>
                          <div className="organization-contact-line">
                            <FiMail /><span>{contact.correo || "No informado"}</span>
                            {contact.correo && <button type="button" onClick={() => copyValue(contact.correo, `mail-${contact.id}`)} title="Copiar correo"><FiCopy /></button>}
                          </div>
                          {copied.endsWith(`-${contact.id}`) && <small className="organization-copied">Copiado</small>}
                        </article>
                      ))}
                    </div>
                  ) : <p className="organization-empty">Todavía no hay contactos profesionales asociados.</p>}
                </section>
              )}

              {activeTab === "trayectoria" && (
                <section className="organization-tab-panel organization-trajectory">
                  <div className="organization-summary-boxes">
                    <div><b>{selected.trayectoria?.length || 0}</b><span>Participaciones</span></div>
                    <div><b>{programs.length}</b><span>Programas</span></div>
                    <div><b>{initiatives.length}</b><span>Iniciativas</span></div>
                  </div>
                  <div className="organization-trajectory-section">
                    <div className="organization-trajectory-heading">
                      <h3><FiUsers /> Programas</h3>
                      {programYears.length > 0 && (
                        <label>
                          <span className="visually-hidden">Filtrar Programas por año</span>
                          <select value={programYear} onChange={(event) => setProgramYear(event.target.value)}>
                            <option value="todos">Todos los años</option>
                            {programYears.map((year) => <option key={year} value={year}>{year}</option>)}
                          </select>
                        </label>
                      )}
                    </div>
                    <TrajectoryList items={filteredPrograms} empty={programYear === "todos" ? "Esta organización no registra programas." : `No hay Programas de ${programYear}.`} />
                  </div>
                  <div className="organization-trajectory-section">
                    <div className="organization-trajectory-heading">
                      <h3><FiBriefcase /> Iniciativas</h3>
                      {initiativeYears.length > 0 && (
                        <label>
                          <span className="visually-hidden">Filtrar Iniciativas por año</span>
                          <select value={initiativeYear} onChange={(event) => setInitiativeYear(event.target.value)}>
                            <option value="todos">Todos los años</option>
                            {initiativeYears.map((year) => <option key={year} value={year}>{year}</option>)}
                          </select>
                        </label>
                      )}
                    </div>
                    <TrajectoryList items={filteredInitiatives} empty={initiativeYear === "todos" ? "Esta organización no registra otras iniciativas." : `No hay Iniciativas de ${initiativeYear}.`} />
                  </div>
                </section>
              )}

              {activeTab === "comentarios" && (
                <section className="organization-tab-panel">
                  <div className="organization-tab-heading">
                    <div><span>SEGUIMIENTO</span><h3>Comentarios</h3></div>
                    <button
                      type="button"
                      className="organization-tab-action"
                      onClick={() => {
                        setCommentError("");
                        setShowCommentForm(true);
                      }}
                      disabled={busy || showCommentForm}
                    >
                      <FiPlus /> Agregar
                    </button>
                  </div>
                  {showCommentForm && (
                    <form className="organization-inline-form" onSubmit={addComment}>
                      {commentError && <Alert variant="danger">{commentError}</Alert>}
                      <label>
                        Nuevo comentario
                        <textarea
                          className="form-control"
                          value={commentText}
                          onChange={(event) => setCommentText(event.target.value)}
                          rows="4"
                          autoFocus
                          required
                        />
                      </label>
                      <div>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            setShowCommentForm(false);
                            setCommentText("");
                            setCommentError("");
                          }}
                          disabled={busy}
                        >
                          Cancelar
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={busy || !commentText.trim()}>
                          {busy && <Spinner animation="border" size="sm" />} Guardar
                        </button>
                      </div>
                    </form>
                  )}
                  {!showCommentForm && commentError && <Alert variant="danger">{commentError}</Alert>}
                  {selected.comentarios?.length ? (
                    <div className="organization-comments-list">
                      {selected.comentarios.map((comment) => {
                        const author = comment.created_by_user;
                        return (
                          <article className="organization-comment-card" key={comment.id}>
                            <div className="organization-comment-author">
                              <ContactAvatar contact={{
                                nombre: author?.name || comment.autor || "Sin autor",
                                imagen: author?.image,
                              }} />
                              <div>
                                <strong>{author?.name || comment.autor || "Sin autor"}</strong>
                                <time dateTime={comment.timestamp}>{formatDateTime(comment.timestamp)}</time>
                              </div>
                            </div>
                            <p>{comment.comment_text}</p>
                          </article>
                        );
                      })}
                    </div>
                  ) : <p className="organization-empty">Todavía no hay comentarios sobre esta organización.</p>}
                </section>
              )}

              {activeTab === "documentos" && (
                <section className="organization-tab-panel">
                  <div className="organization-tab-heading">
                    <div><span>ARCHIVOS</span><h3>Documentos</h3></div>
                    <button
                      type="button"
                      className="organization-tab-action"
                      onClick={() => {
                        setDocumentError("");
                        setShowDocumentForm(true);
                      }}
                      disabled={busy || showDocumentForm}
                    >
                      <FiUpload /> Subir
                    </button>
                  </div>
                  {showDocumentForm && (
                    <form className="organization-inline-form" onSubmit={addDocument}>
                      {documentError && <Alert variant="danger">{documentError}</Alert>}
                      <label>
                        Nombre
                        <input
                          className="form-control"
                          value={documentName}
                          onChange={(event) => setDocumentName(event.target.value)}
                          placeholder="Opcional; se usará el nombre del archivo"
                        />
                      </label>
                      <label>
                        Documento PDF
                        <input
                          className="form-control"
                          type="file"
                          accept="application/pdf,.pdf"
                          onChange={(event) => setDocumentFile(event.target.files?.[0] || null)}
                          required
                        />
                      </label>
                      <div>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            setShowDocumentForm(false);
                            setDocumentName("");
                            setDocumentFile(null);
                            setDocumentError("");
                          }}
                          disabled={busy}
                        >
                          Cancelar
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={busy || !documentFile}>
                          {busy && <Spinner animation="border" size="sm" />} Subir
                        </button>
                      </div>
                    </form>
                  )}
                  {!showDocumentForm && documentError && <Alert variant="danger">{documentError}</Alert>}
                  {selected.documentos?.length ? (
                    <div className="organization-documents-list">
                      {selected.documentos.map((document) => (
                        <a href={document.documento} target="_blank" rel="noreferrer" key={document.id}>
                          <FiFileText />
                          <div>
                            <strong>{document.nombre || "Documento"}</strong>
                            <time dateTime={document.timestamp}>{formatDateTime(document.timestamp)}</time>
                          </div>
                          <FiDownload />
                        </a>
                      ))}
                    </div>
                  ) : <p className="organization-empty">Todavía no hay documentos asociados.</p>}
                </section>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showCreate} onHide={() => !busy && setShowCreate(false)} centered dialogClassName="organization-form-modal">
        <form onSubmit={createOrganization}>
          <Modal.Header closeButton={!busy}><Modal.Title>Agregar organización</Modal.Title></Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <div className="organization-form-section">
              <label>Logo<input className="form-control" type="file" name="logo" accept="image/*" required /></label>
              <label>Nombre<input className="form-control" name="nombre" required /></label>
              <label>Subnombre<input className="form-control" name="subnombre" placeholder="Se exige si el nombre ya existe" /></label>
              <label>Holding<select className="form-select" name="holding_id" defaultValue=""><option value="">Sin holding</option>{organizations.map((item) => <option key={item.id} value={item.id}>{organizationLabel(item)}</option>)}</select></label>
            </div>
          </Modal.Body>
          <Modal.Footer><button type="button" className="btn btn-outline-secondary" onClick={() => setShowCreate(false)} disabled={busy}>Cancelar</button><button type="submit" className="btn btn-primary" disabled={busy}>{busy ? <Spinner animation="border" size="sm" /> : <FiPlus />} Guardar</button></Modal.Footer>
        </form>
      </Modal>

      <Modal show={showField} onHide={() => !busy && setShowField(false)} centered dialogClassName="organization-form-modal">
        <form onSubmit={saveField}>
          <Modal.Header closeButton={!busy}><Modal.Title>Actualizar {editingField === "logo" ? "logo" : editingField}</Modal.Title></Modal.Header>
          <Modal.Body>{error && <Alert variant="danger">{error}</Alert>}<div className="organization-form-section">{renderFieldInput()}</div></Modal.Body>
          <Modal.Footer><button type="button" className="btn btn-outline-secondary" onClick={() => setShowField(false)} disabled={busy}>Cancelar</button><button type="submit" className="btn btn-primary" disabled={busy}>{busy && <Spinner animation="border" size="sm" />} Guardar</button></Modal.Footer>
        </form>
      </Modal>

      <Modal show={showContact} onHide={() => !busy && setShowContact(false)} centered dialogClassName="organization-form-modal">
        <form onSubmit={saveContact}>
          <Modal.Header closeButton={!busy}><Modal.Title>{editingContact ? "Actualizar contacto" : "Agregar contacto"}</Modal.Title></Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <div className="organization-form-section">
              <label>Imagen<input className="form-control" type="file" name="imagen" accept="image/*" onChange={(event) => { setContactImageReplacement(event.target.files?.[0]?.name || ""); setRemoveContactImage(false); }} /></label>
              {contactImageReplacement && <small className="organization-replacement-file"><FiUpload /> Reemplazo seleccionado: {contactImageReplacement}</small>}
              {editingContact?.imagen && !contactImageReplacement && <div className={`organization-current-media${removeContactImage ? " is-removed" : ""}`}>{!removeContactImage && <img src={editingContact.imagen} alt={`Foto de ${editingContact.nombre}`} />}<span><strong>{removeContactImage ? "La imagen se quitará al guardar" : "Imagen actual"}</strong><small>{removeContactImage ? "Puedes deshacer esta decisión." : "Se conservará si no eliges otra."}</small></span><button type="button" onClick={() => setRemoveContactImage((current) => !current)}>{removeContactImage ? <><FiRotateCcw /> Deshacer</> : <><FiTrash2 /> Quitar</>}</button></div>}
              <label>Nombre<input className="form-control" name="nombre" defaultValue={editingContact?.nombre || ""} required /></label>
              <label>Celular<input className="form-control" name="celular" defaultValue={editingContact?.celular || ""} inputMode="numeric" /></label>
              <label>Correo<input className="form-control" type="email" name="correo" defaultValue={editingContact?.correo || ""} /></label>
            </div>
          </Modal.Body>
          <Modal.Footer><button type="button" className="btn btn-outline-secondary" onClick={() => setShowContact(false)} disabled={busy}>Cancelar</button><button type="submit" className="btn btn-primary" disabled={busy}>{busy && <Spinner animation="border" size="sm" />} Guardar</button></Modal.Footer>
        </form>
      </Modal>
    </main>
  );
};

export default OrganizacionesPage;
